import { z } from 'zod'
import { commonStringSchemas } from './common'
import { TITLES } from '../titles'
import countries from 'i18n-iso-countries'

/**
 * Email validation schema
 */
export const emailSchema = commonStringSchemas.email

/**
 * Verification code validation schema
 */
export const verificationCodeSchema = z.object({
  code: z
    .string()
    .min(6, 'Verification code must be 6 digits')
    .max(6, 'Verification code must be 6 digits')
    .regex(/^\d{6}$/, 'Verification code must contain only numbers'),
})

/**
 * Sign-up form validation schema
 */
export const signUpFormSchema = z.object({
  email: emailSchema,
  password: z.string().min(8, 'Password must be at least 8 characters').optional(),
})

/**
 * Sign-in form validation schema
 */
export const signInFormSchema = z.object({
  email: emailSchema,
})

/**
 * Settings profile form validation schema
 */
export const settingsProfileSchema = z.object({
  name: commonStringSchemas.required('Full name'),
  email: commonStringSchemas.email,
  institution: commonStringSchemas.optional(),
  title: z.enum(TITLES, {
    required_error: 'Job title is required',
    invalid_type_error: 'Please select a valid job title',
  }),
  country: commonStringSchemas
    .required('Country')
    .length(3, 'Country code must be ISO Alpha 3')
    .refine((code) => countries.isValid(code), 'Please select a valid country'),
})

/**
 * Organization form validation schema
 */
export const organizationFormSchema = z.object({
  name: commonStringSchemas.required('Organization name'),
  contactEmail: commonStringSchemas.email,
  address: commonStringSchemas.optional(),
  websiteUrl: z.string().url('Invalid website URL').optional().or(z.literal('')),
  description: commonStringSchemas.optional(),
  credentials: z.array(z.string()).optional(),
})

/**
 * Onboarding step validation schemas
 */
export const nameStepSchema = z.object({
  name: commonStringSchemas.required('Full name'),
})

export const institutionStepSchema = z.object({
  institution: commonStringSchemas.required('Institution'),
})

export const countryStepSchema = z.object({
  country: commonStringSchemas
    .required('Country')
    .length(3, 'Country code must be ISO Alpha 3')
    .refine((code) => countries.isValid(code), 'Please select a valid country'),
})

export const titleStepSchema = z.object({
  title: z.enum(TITLES, {
    required_error: 'Job title is required',
    invalid_type_error: 'Please select a valid job title',
  }),
})

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type EmailFormData = z.infer<typeof emailSchema>
export type VerificationCodeFormData = z.infer<typeof verificationCodeSchema>
export type SignUpFormData = z.infer<typeof signUpFormSchema>
export type SignInFormData = z.infer<typeof signInFormSchema>
export type SettingsProfileFormData = z.infer<typeof settingsProfileSchema>
export type OrganizationFormData = z.infer<typeof organizationFormSchema>
export type NameStepData = z.infer<typeof nameStepSchema>
export type InstitutionStepData = z.infer<typeof institutionStepSchema>
export type CountryStepData = z.infer<typeof countryStepSchema>
export type TitleStepData = z.infer<typeof titleStepSchema>
